import { useEffect, createContext, useContext, ReactNode } from 'react';

// Define the types for our head metadata
interface MetaTag {
  name?: string;
  property?: string;
  content: string;
  key?: string;
}

interface LinkTag {
  rel: string;
  href: string;
  type?: string;
  key?: string;
}

interface ScriptTag {
  src?: string;
  innerHTML?: string;
  type?: string;
  async?: boolean;
  defer?: boolean;
  key?: string;
}

interface HeadContextType {
  title: string;
  metas: MetaTag[];
  links: LinkTag[];
  scripts: ScriptTag[];
  setTitle: (title: string) => void;
  addMeta: (meta: MetaTag) => void;
  addLink: (link: LinkTag) => void;
  addScript: (script: ScriptTag) => void;
}

// Create a context to manage head elements
const HeadContext = createContext<HeadContextType>({
  title: '',
  metas: [],
  links: [],
  scripts: [],
  setTitle: () => {},
  addMeta: () => {},
  addLink: () => {},
  addScript: () => {},
});

// Provider component to manage head elements
export function HeadProvider({ children }: { children: ReactNode }) {
  // State is managed in memory for SSG compatibility
  let title = '';
  const metas: MetaTag[] = [];
  const links: LinkTag[] = [];
  const scripts: ScriptTag[] = [];
  
  // Add a flag to track if this instance has initialized
  const isInitializedRef = typeof window !== 'undefined' ? { current: false } : { current: true };

  // Function to set the document title
  const setTitle = (newTitle: string) => {
    title = newTitle;
    if (typeof document !== 'undefined') {
      document.title = newTitle;
    }
  };

  // Function to add a meta tag
  const addMeta = (meta: MetaTag) => {
    const key = meta.key || `meta-${meta.name || meta.property}`;
    const existingIndex = metas.findIndex(m => 
      (m.key === key) || 
      (m.name && m.name === meta.name) || 
      (m.property && m.property === meta.property)
    );
    
    if (existingIndex !== -1) {
      metas[existingIndex] = { ...meta, key };
    } else {
      metas.push({ ...meta, key });
    }
    
    if (typeof document !== 'undefined') {
      updateMetaTags();
    }
  };

  // Function to add a link tag
  const addLink = (link: LinkTag) => {
    const key = link.key || `link-${link.rel}-${link.href}`;
    const existingIndex = links.findIndex(l => 
      (l.key === key) || 
      (l.rel === link.rel && l.href === link.href)
    );
    
    if (existingIndex !== -1) {
      links[existingIndex] = { ...link, key };
    } else {
      links.push({ ...link, key });
    }
    
    if (typeof document !== 'undefined') {
      updateLinkTags();
    }
  };

  // Function to add a script tag
  const addScript = (script: ScriptTag) => {
    const key = script.key || `script-${script.src || 'inline'}`;
    const existingIndex = scripts.findIndex(s => s.key === key);
    
    if (existingIndex !== -1) {
      scripts[existingIndex] = { ...script, key };
    } else {
      scripts.push({ ...script, key });
    }
    
    if (typeof document !== 'undefined') {
      updateScriptTags();
    }
  };

  // Function to update meta tags in the document
  const updateMetaTags = () => {
    if (typeof document === 'undefined') return;

    // Remove existing meta tags that we manage
    const existingTags = document.head.querySelectorAll('meta[data-head-manager]');
    existingTags.forEach(tag => tag.remove());

    // Add current meta tags
    metas.forEach(meta => {
      const tag = document.createElement('meta');
      tag.setAttribute('data-head-manager', 'true');
      
      if (meta.name) tag.setAttribute('name', meta.name);
      if (meta.property) tag.setAttribute('property', meta.property);
      if (meta.content) tag.setAttribute('content', meta.content);
      
      document.head.appendChild(tag);
    });
  };

  // Function to update link tags in the document
  const updateLinkTags = () => {
    if (typeof document === 'undefined') return;

    // Remove existing link tags that we manage
    const existingTags = document.head.querySelectorAll('link[data-head-manager]');
    existingTags.forEach(tag => tag.remove());

    // Add current link tags
    links.forEach(link => {
      const tag = document.createElement('link');
      tag.setAttribute('data-head-manager', 'true');
      tag.setAttribute('rel', link.rel);
      tag.setAttribute('href', link.href);
      if (link.type) tag.setAttribute('type', link.type);
      
      document.head.appendChild(tag);
    });
  };

  // Function to update script tags in the document
  const updateScriptTags = () => {
    if (typeof document === 'undefined') return;

    // Remove existing script tags that we manage
    const existingTags = document.head.querySelectorAll('script[data-head-manager]');
    existingTags.forEach(tag => tag.remove());

    // Add current script tags
    scripts.forEach(script => {
      const tag = document.createElement('script');
      tag.setAttribute('data-head-manager', 'true');
      
      if (script.src) tag.setAttribute('src', script.src);
      if (script.type) tag.setAttribute('type', script.type);
      if (script.async) tag.setAttribute('async', '');
      if (script.defer) tag.setAttribute('defer', '');
      if (script.innerHTML) tag.innerHTML = script.innerHTML;
      
      document.head.appendChild(tag);
    });
  };

  // Update the document when running in the browser
  useEffect(() => {
    if (typeof document !== 'undefined') {
      // Check if another instance has already initialized
      const existingFlag = document.querySelector('meta[name="head-manager-initialized"]');
      
      if (!existingFlag) {
        // Mark as initialized
        const flag = document.createElement('meta');
        flag.setAttribute('name', 'head-manager-initialized');
        flag.setAttribute('content', 'true');
        document.head.appendChild(flag);
        
        isInitializedRef.current = true;
        
        // Only update if we're the first instance
        if (title) document.title = title;
        updateMetaTags();
        updateLinkTags();
        updateScriptTags();
      }
    }
    
    // Cleanup function
    return () => {
      // Only clean up if we're the initializing instance
      if (typeof document !== 'undefined' && isInitializedRef.current) {
        const flag = document.querySelector('meta[name="head-manager-initialized"]');
        if (flag) {
          flag.remove();
        }
      }
    };
  }, []);

  return (
    <HeadContext.Provider
      value={{
        title,
        metas,
        links,
        scripts,
        setTitle,
        addMeta,
        addLink,
        addScript,
      }}
    >
      {children}
    </HeadContext.Provider>
  );
}

// Hook to use the head context
export function useHead() {
  return useContext(HeadContext);
}

// Component to set head elements
interface HeadProps {
  title?: string;
  metas?: MetaTag[];
  links?: LinkTag[];
  scripts?: ScriptTag[];
  children?: ReactNode;
}

export function Head({ title, metas = [], links = [], scripts = [], children }: HeadProps) {
  const head = useHead();

  useEffect(() => {
    // Set title if provided
    if (title) {
      head.setTitle(title);
    }

    // Add meta tags
    metas.forEach(meta => {
      head.addMeta(meta);
    });

    // Add link tags
    links.forEach(link => {
      head.addLink(link);
    });

    // Add script tags
    scripts.forEach(script => {
      head.addScript(script);
    });
  }, [title, metas, links, scripts, head]);

  return <>{children}</>;
}

// Component to generate head elements during SSR
export function HeadManager() {
  const { title, metas, links, scripts } = useHead();

  // This component is only used during SSR
  if (typeof document !== 'undefined') {
    return null;
  }

  return (
    <>
      {title && <title>{title}</title>}
      {metas.map((meta) => (
        <meta
          key={meta.key}
          name={meta.name}
          property={meta.property}
          content={meta.content}
          data-head-manager="true"
        />
      ))}
      {links.map((link) => (
        <link
          key={link.key}
          rel={link.rel}
          href={link.href}
          type={link.type}
          data-head-manager="true"
        />
      ))}
      {scripts.map((script) => (
        <script
          key={script.key}
          src={script.src}
          type={script.type}
          async={script.async}
          defer={script.defer}
          data-head-manager="true"
          dangerouslySetInnerHTML={script.innerHTML ? { __html: script.innerHTML } : undefined}
        />
      ))}
    </>
  );
} 